import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Building2, 
  Activity, 
  Package, 
  Wrench, 
  Users,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronRight,
  BarChart3,
  Layers,
  ArrowLeft,
  RefreshCw,
  Bell,
  Shield,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NetworkDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilityDetails, setFacilityDetails] = useState(null);
  const [viewMode, setViewMode] = useState("overview"); // overview, comparison, details

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("hospital_token");
      if (!token) {
        toast.error("Please log in to access the Network Dashboard");
        navigate("/hospital/login");
        return;
      }

      const response = await axios.get(`${API}/api/network/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      toast.error("Failed to load network dashboard");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchDashboard();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const fetchFacilityDetails = async (facilityId) => {
    try {
      const token = localStorage.getItem("hospital_token");
      const response = await axios.get(`${API}/api/network/facility/${facilityId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacilityDetails(response.data);
      setSelectedFacility(facilityId);
      setViewMode("details");
    } catch (err) {
      console.error("Error fetching facility details:", err);
      toast.error("Failed to load facility details");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading network dashboard...</p>
        </div>
      </div>
    );
  }

  const { health_system, network_totals, facilities, recent_activity, alerts, alert_summary } = dashboardData || {};

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/hospital/portal")}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Layers className="w-6 h-6 text-emerald-500" />
                  Network Dashboard
                </h1>
                <p className="text-sm text-slate-400">{health_system?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Alert Badge */}
              {alert_summary?.high > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <Bell className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400 font-medium">{alert_summary.high} Critical</span>
                </div>
              )}
              <button
                onClick={refreshData}
                disabled={refreshing}
                className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="refresh-dashboard"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <div className="flex bg-slate-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("overview")}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === "overview" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"
                  }`}
                  data-testid="view-overview"
                >
                  Overview
                </button>
                <button
                  onClick={() => setViewMode("alerts")}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors flex items-center gap-1 ${
                    viewMode === "alerts" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"
                  }`}
                  data-testid="view-alerts"
                >
                  Alerts
                  {alert_summary?.total > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      alert_summary.high > 0 ? "bg-red-500" : "bg-amber-500"
                    }`}>
                      {alert_summary.total}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setViewMode("comparison")}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === "comparison" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"
                  }`}
                  data-testid="view-comparison"
                >
                  Compare
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {viewMode === "overview" && (
          <>
            {/* Network Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={Building2}
                label="Total Facilities"
                value={network_totals?.total_facilities || 0}
                color="blue"
              />
              <StatCard
                icon={Wrench}
                label="Maintenance Requests"
                value={network_totals?.total_maintenance_requests || 0}
                subValue={`${network_totals?.open_maintenance || 0} open`}
                color="amber"
              />
              <StatCard
                icon={Package}
                label="Backorder Requests"
                value={network_totals?.total_backorders || 0}
                subValue={`${network_totals?.pending_backorders || 0} pending`}
                color="purple"
              />
              <StatCard
                icon={Activity}
                label="Total Assets"
                value={network_totals?.total_assets || 0}
                subValue={`${network_totals?.assets_needing_attention || 0} need attention`}
                color="emerald"
              />
            </div>

            {/* Facilities Grid */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-500" />
                Facilities Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {facilities?.map((facility) => (
                  <FacilityCard
                    key={facility.facility_id}
                    facility={facility}
                    onViewDetails={() => fetchFacilityDetails(facility.facility_id)}
                  />
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-500" />
                Recent Network Activity
              </h2>
              <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                {recent_activity?.length > 0 ? (
                  <div className="divide-y divide-slate-800">
                    {recent_activity.map((activity, idx) => (
                      <ActivityRow key={idx} activity={activity} />
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    No recent activity across the network
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {viewMode === "alerts" && (
          <AlertsPanel alerts={alerts} alertSummary={alert_summary} />
        )}

        {viewMode === "comparison" && (
          <FacilityComparison facilities={facilities} />
        )}

        {viewMode === "details" && facilityDetails && (
          <FacilityDetailsView
            details={facilityDetails}
            onBack={() => setViewMode("overview")}
          />
        )}
      </main>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, color }) {
  const colorClasses = {
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5" />
        <span className="text-sm text-slate-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  );
}

// Facility Card Component
function FacilityCard({ facility, onViewDetails }) {
  const { name, type, maintenance, backorders, assets } = facility;
  
  const hasIssues = maintenance?.open > 0 || backorders?.pending > 0;

  return (
    <div 
      className="bg-slate-900 rounded-xl border border-slate-800 p-5 hover:border-slate-700 transition-all cursor-pointer group"
      onClick={onViewDetails}
      data-testid={`facility-card-${facility.facility_id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-white group-hover:text-emerald-400 transition-colors">
            {name}
          </h3>
          <span className="text-xs text-slate-500 capitalize">{type}</span>
        </div>
        {hasIssues && (
          <span className="px-2 py-1 bg-amber-500/10 text-amber-400 text-xs rounded-full">
            Needs Attention
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="p-2 bg-slate-800/50 rounded-lg">
          <div className="text-lg font-semibold text-white">{maintenance?.total || 0}</div>
          <div className="text-xs text-slate-500">Maintenance</div>
          {maintenance?.open > 0 && (
            <div className="text-xs text-amber-400">{maintenance.open} open</div>
          )}
        </div>
        <div className="p-2 bg-slate-800/50 rounded-lg">
          <div className="text-lg font-semibold text-white">{backorders?.total || 0}</div>
          <div className="text-xs text-slate-500">Backorders</div>
          {backorders?.pending > 0 && (
            <div className="text-xs text-amber-400">{backorders.pending} pending</div>
          )}
        </div>
        <div className="p-2 bg-slate-800/50 rounded-lg">
          <div className="text-lg font-semibold text-white">{assets?.total || 0}</div>
          <div className="text-xs text-slate-500">Assets</div>
        </div>
      </div>
      
      <div className="mt-4 flex items-center justify-end text-sm text-slate-500 group-hover:text-emerald-400">
        View Details <ChevronRight className="w-4 h-4 ml-1" />
      </div>
    </div>
  );
}

// Activity Row Component
function ActivityRow({ activity }) {
  const { type, description, status, facility_name, created_at } = activity;
  
  const statusColors = {
    open: "text-amber-400 bg-amber-500/10",
    pending: "text-amber-400 bg-amber-500/10",
    in_progress: "text-blue-400 bg-blue-500/10",
    completed: "text-emerald-400 bg-emerald-500/10",
    fulfilled: "text-emerald-400 bg-emerald-500/10"
  };

  return (
    <div className="p-4 hover:bg-slate-800/50 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${type === "maintenance" ? "bg-amber-500/10" : "bg-purple-500/10"}`}>
          {type === "maintenance" ? (
            <Wrench className="w-4 h-4 text-amber-400" />
          ) : (
            <Package className="w-4 h-4 text-purple-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white truncate">{description}</p>
          <p className="text-xs text-slate-500">{facility_name}</p>
        </div>
        <div className="text-right">
          <span className={`px-2 py-1 rounded-full text-xs capitalize ${statusColors[status?.toLowerCase()] || "text-slate-400 bg-slate-800"}`}>
            {status}
          </span>
          <p className="text-xs text-slate-600 mt-1">
            {created_at ? new Date(created_at).toLocaleDateString() : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

// Facility Comparison View
function FacilityComparison({ facilities }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-emerald-500" />
        Facility Comparison
      </h2>
      
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left p-4 text-slate-400 font-medium">Facility</th>
                <th className="text-center p-4 text-slate-400 font-medium">Maintenance</th>
                <th className="text-center p-4 text-slate-400 font-medium">Open Issues</th>
                <th className="text-center p-4 text-slate-400 font-medium">Backorders</th>
                <th className="text-center p-4 text-slate-400 font-medium">Assets</th>
                <th className="text-center p-4 text-slate-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {facilities?.map((facility) => {
                const hasOpenIssues = facility.maintenance?.open > 0 || facility.backorders?.pending > 0;
                return (
                  <tr key={facility.facility_id} className="hover:bg-slate-800/30">
                    <td className="p-4">
                      <div className="font-medium text-white">{facility.name}</div>
                      <div className="text-xs text-slate-500 capitalize">{facility.type}</div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-lg font-semibold text-white">{facility.maintenance?.total || 0}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`text-lg font-semibold ${facility.maintenance?.open > 0 ? "text-amber-400" : "text-emerald-400"}`}>
                        {facility.maintenance?.open || 0}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-lg font-semibold text-white">{facility.backorders?.total || 0}</span>
                      {facility.backorders?.pending > 0 && (
                        <span className="text-xs text-amber-400 ml-1">({facility.backorders.pending})</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-lg font-semibold text-white">{facility.assets?.total || 0}</span>
                    </td>
                    <td className="p-4 text-center">
                      {hasOpenIssues ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 text-amber-400 rounded-full text-xs">
                          <AlertCircle className="w-3 h-3" />
                          Attention
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-xs">
                          <CheckCircle className="w-3 h-3" />
                          Healthy
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Facility Details View
function FacilityDetailsView({ details, onBack }) {
  const { facility, maintenance_requests, backorder_requests, assets, users, stats } = details;

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
        data-testid="back-to-overview"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Overview
      </button>

      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-emerald-500/10 rounded-xl">
          <Building2 className="w-8 h-8 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">{facility?.name}</h2>
          <p className="text-slate-400 capitalize">{facility?.type} Facility</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-white">{stats?.total_maintenance}</div>
          <div className="text-sm text-slate-400">Maintenance Requests</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-white">{stats?.total_backorders}</div>
          <div className="text-sm text-slate-400">Backorder Requests</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-white">{stats?.total_assets}</div>
          <div className="text-sm text-slate-400">Registered Assets</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-white">{stats?.total_users}</div>
          <div className="text-sm text-slate-400">Registered Users</div>
        </div>
      </div>

      {/* Recent Maintenance */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Wrench className="w-5 h-5 text-amber-400" />
          Recent Maintenance Requests
        </h3>
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          {maintenance_requests?.length > 0 ? (
            <div className="divide-y divide-slate-800">
              {maintenance_requests.slice(0, 5).map((req, idx) => (
                <div key={idx} className="p-4 hover:bg-slate-800/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white">{req.description?.substring(0, 80)}...</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {req.created_at ? new Date(req.created_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs capitalize ${
                      req.status === "completed" ? "bg-emerald-500/10 text-emerald-400" :
                      req.status === "open" ? "bg-amber-500/10 text-amber-400" :
                      "bg-slate-700 text-slate-400"
                    }`}>
                      {req.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500">No maintenance requests</div>
          )}
        </div>
      </div>

      {/* Users List */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-400" />
          Facility Users
        </h3>
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          {users?.length > 0 ? (
            <div className="divide-y divide-slate-800">
              {users.map((user, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                  <span className="text-xs text-slate-400">{user.title || user.department || "Staff"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500">No users registered at this facility</div>
          )}
        </div>
      </div>
    </div>
  );
}


// Alerts Panel Component
function AlertsPanel({ alerts, alertSummary }) {
  const alertTypeConfig = {
    overdue_pm: {
      icon: Calendar,
      label: "Overdue PM",
      color: "text-red-400 bg-red-500/10 border-red-500/20"
    },
    expiring_warranty: {
      icon: Shield,
      label: "Expiring Warranty",
      color: "text-amber-400 bg-amber-500/10 border-amber-500/20"
    },
    maintenance_backlog: {
      icon: Wrench,
      label: "Maintenance Backlog",
      color: "text-orange-400 bg-orange-500/10 border-orange-500/20"
    },
    backorder_delay: {
      icon: Package,
      label: "Backorder Delay",
      color: "text-purple-400 bg-purple-500/10 border-purple-500/20"
    },
    stale_backorder: {
      icon: Clock,
      label: "Stale Backorder",
      color: "text-pink-400 bg-pink-500/10 border-pink-500/20"
    }
  };

  const severityColors = {
    high: "border-l-red-500 bg-red-500/5",
    medium: "border-l-amber-500 bg-amber-500/5",
    low: "border-l-blue-500 bg-blue-500/5"
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Bell className="w-5 h-5 text-emerald-500" />
        Facility Alerts
        <span className="text-sm font-normal text-slate-400">Early Warning System</span>
      </h2>

      {/* Alert Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-400">{alertSummary?.high || 0}</div>
          <div className="text-xs text-slate-400">High Priority</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{alertSummary?.medium || 0}</div>
          <div className="text-xs text-slate-400">Medium Priority</div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-400">{alertSummary?.low || 0}</div>
          <div className="text-xs text-slate-400">Low Priority</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{alertSummary?.by_type?.overdue_pm || 0}</div>
          <div className="text-xs text-slate-400">Overdue PMs</div>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{alertSummary?.by_type?.expiring_warranty || 0}</div>
          <div className="text-xs text-slate-400">Expiring Warranties</div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {alerts?.length > 0 ? (
          <div className="divide-y divide-slate-800">
            {alerts.map((alert, idx) => {
              const config = alertTypeConfig[alert.type] || { icon: AlertTriangle, label: alert.type, color: "text-slate-400 bg-slate-800" };
              const Icon = config.icon;
              
              return (
                <div 
                  key={idx} 
                  className={`p-4 border-l-4 ${severityColors[alert.severity]} hover:bg-slate-800/50 transition-colors`}
                  data-testid={`alert-${alert.type}-${idx}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          alert.severity === "high" ? "bg-red-500/20 text-red-400" :
                          alert.severity === "medium" ? "bg-amber-500/20 text-amber-400" :
                          "bg-blue-500/20 text-blue-400"
                        }`}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-xs text-slate-500">{config.label}</span>
                      </div>
                      <p className="text-sm text-white font-medium">{alert.message}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {alert.facility_name}
                        </span>
                        {alert.asset_name && (
                          <span className="flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {alert.asset_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {alert.days_overdue && (
                        <span className="text-xs text-red-400">{alert.days_overdue}d overdue</span>
                      )}
                      {alert.days_until_expiry && (
                        <span className="text-xs text-amber-400">{alert.days_until_expiry}d left</span>
                      )}
                      {alert.days_pending && (
                        <span className="text-xs text-purple-400">{alert.days_pending}d pending</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-white">All Clear!</p>
            <p className="text-sm text-slate-500">No alerts across your network</p>
          </div>
        )}
      </div>
    </div>
  );
}

